import { useState, useCallback } from 'react';
import { ScreenDataMap, ScreenState, ScreenType } from '../types';

type AnyScreenState = {
  [K in ScreenType]: ScreenState<K>;
}[ScreenType];

interface UseScreensReturn {
  screenType: ScreenType;
  data?: ScreenDataMap[ScreenType];
  setScreen: <T extends ScreenType>(params: { screenType: T; data?: ScreenDataMap[T] }) => void;
  reset: () => void;
}
/**
 * Manages which “screen” is active and an optional payload.
 *
 * @param defaultScreen – the ScreenType to show initially
 */

export const useScreens = (defaultScreen: ScreenType): UseScreensReturn => {
  const [state, setState] = useState<AnyScreenState>({
    screenType: defaultScreen,
    data: undefined,
  });

  const setScreen = useCallback(<T extends ScreenType>(params: { screenType: T; data?: ScreenDataMap[T] }) => {
    return setState(params as AnyScreenState);
  }, []);

  const reset = useCallback(() => {
    setState({ screenType: defaultScreen, data: undefined });
  }, [defaultScreen]);

  return {
    screenType: state.screenType,
    data: state.data,
    setScreen,
    reset,
  };
};
